import { accountStore } from "@features/account/account-store"
import { getPasswordResetToken, PASSWORD_RESET_PARAMS } from "@features/account/password-reset-link"
import { flyingCarpetStore } from "@features/flying-carpet/flying-carpet"
import { useCheckoutConfig } from "@features/tickets/useCheckoutConfig"
import { useEffect } from "react"

const ACTION_PARAM = "spektrix-action"

type SpektrixAction = {
  /** Parameters the action reads, which are removed from the address bar along with the action. */
  params: string[]
  run(params: URLSearchParams, webApiBaseUrl: string): void
}

/**
 * Spektrix sends customers back to the site from its emails and from the links set in Website Admin.
 * Point those at any page with `?spektrix-action=<name>` to open the matching part of the site.
 */
const ACTIONS: Record<string, SpektrixAction> = {
  "reset-password": {
    params: PASSWORD_RESET_PARAMS,
    run(params, webApiBaseUrl) {
      const token = getPasswordResetToken(params)
      // Without a token (e.g. Spektrix's own reset page redirected here), the customer needs a fresh link.
      if (!token) return flyingCarpetStore.open("account/forgot-password")
      accountStore.passwordResetToken = token
      void accountStore.loadCustomer(webApiBaseUrl)
      flyingCarpetStore.open("account/reset-password")
    },
  },
}

/** Runs the Spektrix action in the page's URL, once the ticketing API address is known. */
export function useSpektrixActions() {
  const webApiBaseUrl = useCheckoutConfig().data?.webApiBaseUrl

  useEffect(() => {
    if (!webApiBaseUrl) return
    const url = new URL(window.location.href)
    const name = url.searchParams.get(ACTION_PARAM)
    if (!name) return
    const params = new URLSearchParams(url.search)
    const action = Object.hasOwn(ACTIONS, name) ? ACTIONS[name] : undefined

    // Clear the action first, so a refresh or shared URL doesn't repeat it and tokens don't stay in history.
    for (const param of [ACTION_PARAM, ...(action?.params ?? [])]) url.searchParams.delete(param)
    window.history.replaceState(window.history.state, "", url)

    action?.run(params, webApiBaseUrl)
  }, [webApiBaseUrl])
}
